/**
 * 灯具独立站 - 移动端优先主题 JavaScript
 * Mobile-First Lighting E-commerce Theme JavaScript
 */

// ========== 移动端菜单控制 ==========
class MobileMenu {
  constructor() {
    this.toggle = document.querySelector('.menu-toggle');
    this.menu = document.querySelector('.mobile-menu');
    this.init();
  }

  init() {
    if (this.toggle && this.menu) {
      this.toggle.addEventListener('click', () => this.toggleMenu());
      
      // 点击菜单外部关闭
      document.addEventListener('click', (e) => {
        if (!this.menu.contains(e.target) && !this.toggle.contains(e.target)) {
          this.closeMenu();
        }
      });
    }
  }

  toggleMenu() {
    this.toggle.classList.toggle('active');
    this.menu.classList.toggle('active');
    document.body.style.overflow = this.menu.classList.contains('active') ? 'hidden' : '';
  }

  closeMenu() {
    this.toggle.classList.remove('active');
    this.menu.classList.remove('active');
    document.body.style.overflow = '';
  }
}

// ========== 轮播图控制 ==========
class HeroSlider {
  constructor(container) {
    this.container = container;
    this.slides = container.querySelectorAll('.hero-slider__slide');
    this.dots = container.querySelectorAll('.hero-slider__dot'); // 旧的 dots
    this.capsules = container.querySelectorAll('.capsule-item'); // 新的 capsule
    this.capsuleBg = container.querySelector('.capsule-bg');
    this.currentIndex = 0;
    this.interval = null;
    this.init();
  }

  init() {
    if (this.slides.length === 0) return;

    // 初始化第一张幻灯片
    this.showSlide(0);

    // 获取自动播放设置
    const autoplayEnabled = this.container.dataset.autoplay === 'true';
    const intervalSeconds = parseInt(this.container.dataset.interval) || 5;
    
    // 设置自动播放
    if (autoplayEnabled) {
      this.startAutoPlay(intervalSeconds);
    }

    // 胶囊点击切换
    this.capsules.forEach((cap, index) => {
      cap.addEventListener('click', () => {
        this.showSlide(index);
        this.resetAutoPlay();
      });
    });

    // 旧 dots 点击切换 (兼容)
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.showSlide(index);
        this.resetAutoPlay();
      });
    });

    // 触摸滑动支持（移动端）
    this.addTouchSupport();
  }

  showSlide(index) {
    // 隐藏所有幻灯片
    this.slides.forEach(slide => slide.classList.remove('active'));
    this.dots.forEach(dot => dot.classList.remove('active'));
    this.capsules.forEach(cap => cap.classList.remove('active'));

    // 显示当前幻灯片
    if (this.slides[index]) {
      this.slides[index].classList.add('active');
    }
    if (this.dots[index]) {
      this.dots[index].classList.add('active');
    }
    
    // 更新胶囊状态
    if (this.capsules[index]) {
      this.capsules[index].classList.add('active');
      this.updateCapsuleBg(this.capsules[index]);
    }

    this.currentIndex = index;
  }

  updateCapsuleBg(target) {
    if (this.capsuleBg) {
      this.capsuleBg.style.width = `${target.offsetWidth}px`;
      this.capsuleBg.style.transform = `translateX(${target.offsetLeft}px)`;
      this.capsuleBg.style.opacity = '1';
    }
  }

  nextSlide() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.showSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.showSlide(prevIndex);
  }

  startAutoPlay(intervalSeconds = 5) {
    // intervalSeconds 是秒，需要转换为毫秒
    const intervalMs = intervalSeconds * 1000;
    this.interval = setInterval(() => {
      this.nextSlide();
    }, intervalMs);
  }

  resetAutoPlay() {
    const intervalSeconds = parseInt(this.container.dataset.interval) || 5;
    clearInterval(this.interval);
    this.startAutoPlay(intervalSeconds);
  }

  addTouchSupport() {
    let touchStartX = 0;
    let touchEndX = 0;

    this.container.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    });

    this.container.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    });

    this.handleSwipe = () => {
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
        this.resetAutoPlay();
      }
    };
  }
}

// ========== 产品快速添加到购物车 ==========
class QuickAdd {
  constructor() {
    this.buttons = document.querySelectorAll('.product-card__quick-add');
    this.init();
  }

  init() {
    this.buttons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleQuickAdd(button);
      });
      
      // 移动端触摸支持
      button.addEventListener('touchend', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleQuickAdd(button);
      }, { passive: false });
    });
  }

  async handleQuickAdd(button) {
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId || productId;

    if (!productId) {
      console.error('Product ID not found');
      return;
    }

    // 显示加载状态
    const originalHTML = button.innerHTML;
    button.innerHTML = '<div class="loading"></div>';
    button.disabled = true;

    try {
      // 添加到购物车
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }]
        })
      });

      if (response.ok) {
        // 更新购物车图标
        this.updateCartCount();
        
        // 显示成功提示
        this.showNotification('Product added to cart!', 'success');
        
        // 触发购物车更新事件
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showNotification('Failed to add product. Please try again.', 'error');
    } finally {
      button.innerHTML = originalHTML;
      button.disabled = false;
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const badge = document.querySelector('.header__cart-badge');
      
      if (badge) {
        if (cart.item_count > 0) {
          badge.textContent = cart.item_count;
          badge.style.display = 'flex';
        } else {
          badge.style.display = 'none';
        }
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  showNotification(message, type = 'success') {
    // 创建通知元素
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 80px;
      right: 20px;
      background: ${type === 'success' ? '#4CAF50' : '#f44336'};
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;

    document.body.appendChild(notification);

    // 3秒后移除
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }
}

// ========== 滚动动画 ==========
class ScrollAnimation {
  constructor() {
    this.elements = document.querySelectorAll('[data-animate]');
    this.init();
  }

  init() {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      this.elements.forEach(el => observer.observe(el));
    }
  }
}

// ========== 灯具氛围效果 ==========
class LightingEffect {
  constructor() {
    this.container = document.querySelector('.lighting-effect');
    if (this.container) {
      this.init();
    }
  }

  init() {
    // 根据时间调整灯光效果
    const hour = new Date().getHours();
    this.adjustLighting(hour);

    // 每30分钟更新一次
    setInterval(() => {
      const currentHour = new Date().getHours();
      this.adjustLighting(currentHour);
    }, 30 * 60 * 1000);
  }

  adjustLighting(hour) {
    if (!this.container) return;

    // 早上 (6-12): 明亮
    // 下午 (12-18): 温暖
    // 晚上 (18-24): 柔和
    // 深夜 (0-6): 昏暗

    let opacity = 0.5;
    let color1 = 'rgba(255, 215, 0, 0.1)';
    let color2 = 'rgba(255, 165, 0, 0.1)';

    if (hour >= 6 && hour < 12) {
      opacity = 0.8;
      color1 = 'rgba(255, 255, 200, 0.2)';
      color2 = 'rgba(255, 220, 100, 0.15)';
    } else if (hour >= 12 && hour < 18) {
      opacity = 0.7;
      color1 = 'rgba(255, 215, 0, 0.15)';
      color2 = 'rgba(255, 165, 0, 0.12)';
    } else if (hour >= 18 && hour < 24) {
      opacity = 0.6;
      color1 = 'rgba(255, 200, 50, 0.12)';
      color2 = 'rgba(255, 140, 0, 0.1)';
    } else {
      opacity = 0.3;
      color1 = 'rgba(255, 215, 0, 0.05)';
      color2 = 'rgba(255, 165, 0, 0.05)';
    }

    this.container.style.background = `
      radial-gradient(circle at 20% 50%, ${color1} 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, ${color2} 0%, transparent 50%)
    `;
    this.container.style.opacity = opacity;
  }
}

// ========== Header 滚动隐藏（移动端） ==========
class ScrollHeader {
  constructor() {
    this.header = document.querySelector('.header');
    this.lastScrollY = window.scrollY;
    this.scrollingDown = false;
    
    if (this.header && window.innerWidth <= 767) {
      this.init();
    }
  }

  init() {
    let ticking = false;
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  handleScroll() {
    const currentScrollY = window.scrollY;
    const scrollThreshold = 100; // 滚动超过 100px 后才隐藏

    if (currentScrollY > scrollThreshold) {
      if (currentScrollY > this.lastScrollY && !this.scrollingDown) {
        // 向下滚动
        this.header.classList.add('hide-on-scroll');
        this.scrollingDown = true;
      } else if (currentScrollY < this.lastScrollY && this.scrollingDown) {
        // 向上滚动
        this.header.classList.remove('hide-on-scroll');
        this.scrollingDown = false;
      }
    } else {
      this.header.classList.remove('hide-on-scroll');
      this.scrollingDown = false;
    }

    this.lastScrollY = currentScrollY;
  }
}

// ========== 初始化 ==========
document.addEventListener('DOMContentLoaded', () => {
  // 初始化移动端菜单
  new MobileMenu();

  // 初始化轮播图
  const sliderContainers = document.querySelectorAll('.hero-slider');
  sliderContainers.forEach(container => {
    new HeroSlider(container);
  });

  // 初始化快速添加到购物车
  new QuickAdd();

  // 初始化滚动动画
  new ScrollAnimation();

  // 初始化灯具氛围效果
  new LightingEffect();
  
  // 初始化滚动隐藏 header
  new ScrollHeader();
});

// ========== 购物车更新监听 ==========
document.addEventListener('cart:updated', () => {
  // 更新购物车数量
  const quickAdd = new QuickAdd();
  quickAdd.updateCartCount();
});

